import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Ticket, Plus, MessageSquare, Clock, CheckCircle, AlertTriangle,
  Send, User
} from 'lucide-react';

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [response, setResponse] = useState('');
  const [sending, setSending] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'technical',
    priority: 'medium'
  });

  const isAdmin = ['super_admin', 'practice_admin'].includes(user?.role);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/tickets', newTicket);
      toast.success('Support ticket created');
      setShowNewTicket(false);
      setNewTicket({ subject: '', description: '', category: 'technical', priority: 'medium' });
      loadTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleRespond = async () => {
    if (!response.trim() || !selectedTicket) return;

    setSending(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/respond`, { response });
      toast.success('Response sent');
      setResponse('');
      loadTicketDetail(selectedTicket.id);
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await api.patch(`/tickets/${ticketId}/status`, null, { params: { status } });
      toast.success('Status updated');
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-[#991B1B] text-white';
      case 'high': return 'bg-[#92400E] text-white';
      case 'medium': return 'bg-[#1A56DB] text-white';
      default: return 'bg-[#64748B] text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-[#0E7490]/10 text-[#0E7490]';
      case 'in_progress': return 'bg-[#92400E]/10 text-[#92400E]';
      case 'resolved': return 'bg-[#166534]/10 text-[#166534]';
      case 'closed': return 'bg-[#64748B]/10 text-[#64748B]';
      default: return 'bg-[#64748B]/10 text-[#64748B]';
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isAdmin ? 'Support Dashboard' : 'Support'}
          </h1>
          <p className="text-[#64748B] mt-1">
            {isAdmin ? 'Manage support tickets from all users' : 'Get help with any issues'}
          </p>
        </div>
        <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
          <DialogTrigger asChild>
            <Button className="careiq-btn-primary flex items-center gap-2" data-testid="new-ticket-btn">
              <Plus className="w-4 h-4" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="careiq-label">Subject *</Label>
                <Input
                  placeholder="Brief description of the issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  className="careiq-input"
                  data-testid="ticket-subject"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="careiq-label">Category</Label>
                  <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                    <SelectTrigger className="careiq-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="billing">Billing Question</SelectItem>
                      <SelectItem value="clinical">Clinical Support</SelectItem>
                      <SelectItem value="access">Access/Permissions</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="careiq-label">Priority</Label>
                  <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                    <SelectTrigger className="careiq-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="careiq-label">Description *</Label>
                <Textarea
                  placeholder="Please describe the issue in detail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="careiq-input min-h-[120px]"
                  data-testid="ticket-description"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowNewTicket(false)}>Cancel</Button>
                <Button className="careiq-btn-primary" onClick={handleCreateTicket} data-testid="submit-ticket-btn">
                  Submit Ticket
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <Card className="lg:col-span-1 careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#0E7490]" />
              Tickets ({tickets.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : tickets.length > 0 ? (
                <div className="divide-y divide-[#E2E8F0]">
                  {tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      onClick={() => loadTicketDetail(ticket.id)}
                      className={`w-full p-4 text-left hover:bg-[#F8FAFC] transition-colors ${
                        selectedTicket?.id === ticket.id ? 'bg-[#0E7490]/5' : ''
                      }`}
                      data-testid={`ticket-item-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#0A2342] truncate">{ticket.subject}</div>
                          <div className="text-xs text-[#64748B] mt-1">
                            {ticket.ticket_number} • {ticket.created_by_name}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className="text-xs text-[#94A3B8]">
                          {ticket.response_count} replies
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#64748B]">
                  <Ticket className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                  <p className="text-sm">No tickets yet</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Ticket Detail */}
        <Card className="lg:col-span-2 careiq-card">
          <CardContent className="p-6 min-h-[500px]">
            {selectedTicket ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#64748B]">{selectedTicket.ticket_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-[#0A2342] mt-2">{selectedTicket.subject}</h2>
                    <p className="text-sm text-[#64748B] mt-1">
                      Submitted by {selectedTicket.created_by_name} • {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                  {isAdmin && (
                    <Select value={selectedTicket.status} onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Original Description */}
                <div className="p-4 bg-[#F8FAFC] rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2">
                    <User className="w-4 h-4" />
                    {selectedTicket.created_by_name}
                  </div>
                  <p className="text-[#334155] whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Responses */}
                {selectedTicket.responses?.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-[#0A2342]">Responses</h3>
                    {selectedTicket.responses.map((res, idx) => (
                      <div key={idx} className="p-4 bg-[#0E7490]/5 rounded-lg border-l-4 border-[#0E7490]">
                        <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2">
                          <User className="w-4 h-4" />
                          {res.responded_by_name} (Support)
                          <span>•</span>
                          <span>{new Date(res.responded_at).toLocaleString()}</span>
                        </div>
                        <p className="text-[#334155] whitespace-pre-wrap">{res.response}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Admin Reply */}
                {isAdmin && selectedTicket.status !== 'closed' && (
                  <div className="pt-4 border-t border-[#E2E8F0]">
                    <Label className="careiq-label">Reply</Label>
                    <Textarea
                      placeholder="Type your response..."
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      className="careiq-input min-h-[100px]"
                      data-testid="ticket-response"
                    />
                    <div className="flex justify-end mt-3">
                      <Button 
                        className="careiq-btn-primary flex items-center gap-2"
                        onClick={handleRespond}
                        disabled={sending || !response.trim()}
                        data-testid="send-response-btn"
                      >
                        {sending ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send Response
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-[#64748B]">
                <MessageSquare className="w-16 h-16 text-[#94A3B8] mb-4" />
                <p className="text-lg font-medium text-[#0A2342]">Select a ticket</p>
                <p className="text-sm">Choose a ticket from the list to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
